import React, { useEffect, useState } from "react";
import Header from "./shared/Header";
import Footer from "./shared/Footer";
import { ArrowRight, Search, Code2, Activity } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { loadStripe } from "@stripe/stripe-js";
import apiClient from '../utils/apiClient';
import { toast } from 'react-toastify';
import axios from "axios";
import {BASE_URL} from "../config"

// Stripe: preserved for existing customers with in-flight sessions.
// Not called from the new consulting pricing UI.
const stripePromise = loadStripe(
  "pk_live_51QL9nuBySApRicVUmFsIkWlf1K97iYbRWAIY49E0096xYc0TFj7UYtvuzscKG1rC61eSohsOs2h35wVbgeDhMxlt00T0h2wUZE"
);

const handleCheckout = async (plan, navigate, setLoading) => {
  if (plan !== "basic") {
    localStorage.setItem('pricing', 'professional');
  }
  setLoading(true);
  const check = await apiClient.post('/payment-check', {
    email: localStorage.getItem('databackfill-useremail')
  });
  localStorage.setItem('pricing', '');
  if (check.paid === 'paid') {
    toast.error('You already paid!');
    setLoading(false);
    return;
  }
  if (plan === "basic") {
    navigate('/auth');
    return;
  } else {
    localStorage.setItem('pricing', 'professional');
    const response = await apiClient.post(`/api/create-checkout-session`, {
      email: localStorage.getItem('databackfill-useremail')
    });
    const stripe = await stripePromise;
    await stripe.redirectToCheckout({ sessionId: response.sessionId });
  }
  setLoading(false);
};

const engagements = [
  {
    icon: <Search size={24} />,
    label: '2 weeks · Fixed scope',
    title: 'Discovery Sprint',
    description:
      'For teams that need to validate direction before committing to a build. We scope the problem, map the architecture, and deliver a concrete technical plan.',
    outcome: 'Technical plan, architecture diagram, rough estimate, and a clear go / no-go recommendation.',
    cta: 'Book a sprint',
  },
  {
    icon: <Code2 size={24} />,
    label: '6–12 weeks · Scoped statement of work',
    title: 'Build Engagement',
    description:
      'For shipping a defined system end-to-end. We take ownership of delivery: architecture, engineering, integration, and handoff.',
    outcome: 'Working software in production, documentation, and a handoff session or transition into retainer.',
    cta: 'Scope a project',
    featured: true,
  },
  {
    icon: <Activity size={24} />,
    label: 'Ongoing · Month-to-month',
    title: 'Operate / Retainer',
    description:
      'For teams who want us embedded post-launch to evolve the system, respond to incidents, and keep the work moving.',
    outcome: 'Dedicated capacity, regular check-ins, and a team that already knows your stack.',
    cta: 'Start a conversation',
  },
];

const PricingPage = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    localStorage.setItem('pricing', '');
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white overflow-hidden">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Page header */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-16 bg-gradient-to-br from-blue-50 via-white to-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm font-medium mb-6">
            Engagements
          </span>
          <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            How we engage.
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-xl mx-auto leading-relaxed">
            Three shapes of work. All priced based on scope. No tiers.
          </p>
        </div>
      </section>

      {/* Engagement model cards */}
      <section className="py-12 md:py-20">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-start">
            {engagements.map((eng) => (
              <div
                key={eng.title}
                className={`rounded-2xl p-8 flex flex-col h-full ${
                  eng.featured
                    ? 'bg-blue-600 shadow-xl ring-2 ring-blue-600'
                    : 'bg-white border border-gray-200 shadow-sm'
                }`}
              >
                {eng.featured && (
                  <div className="text-xs font-semibold text-blue-200 uppercase tracking-widest mb-4">
                    Most common
                  </div>
                )}
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${
                    eng.featured ? 'bg-blue-500 text-white' : 'bg-blue-50 text-blue-600'
                  }`}
                >
                  {eng.icon}
                </div>
                <p className={`text-xs font-medium mb-2 ${eng.featured ? 'text-blue-200' : 'text-gray-400'}`}>
                  {eng.label}
                </p>
                <h2 className={`text-2xl font-bold mb-4 ${eng.featured ? 'text-white' : 'text-gray-900'}`}>
                  {eng.title}
                </h2>
                <p className={`text-sm leading-relaxed mb-6 flex-grow ${eng.featured ? 'text-blue-100' : 'text-gray-600'}`}>
                  {eng.description}
                </p>
                <div className={`rounded-xl p-4 mb-8 ${eng.featured ? 'bg-blue-500' : 'bg-gray-50'}`}>
                  <p className={`text-xs font-semibold uppercase tracking-widest mb-2 ${eng.featured ? 'text-blue-200' : 'text-gray-400'}`}>
                    What you get
                  </p>
                  <p className={`text-sm leading-relaxed ${eng.featured ? 'text-blue-100' : 'text-gray-600'}`}>
                    {eng.outcome}
                  </p>
                </div>
                <Link
                  to="/contact"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className={`inline-flex items-center justify-center w-full py-3 rounded-xl font-medium text-sm transition-all ${
                    eng.featured
                      ? 'bg-white text-blue-600 hover:bg-blue-50'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {eng.cta}
                  <ArrowRight className="ml-2" size={16} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How pricing works */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-xl font-bold text-gray-900 mb-4">How pricing works</h2>
            <p className="text-gray-600 leading-relaxed">
              Scope drives price. A discovery sprint is fixed.
              A build engagement is scoped before we start. A retainer is sized to the ongoing need.
              Every engagement starts with a conversation.
            </p>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-12 md:py-20">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-blue-600 rounded-3xl p-6 md:p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500 rounded-full transform translate-x-1/2 -translate-y-1/2 blur-2xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-700 rounded-full transform -translate-x-1/2 translate-y-1/2 blur-2xl" />
            <div className="relative max-w-lg mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Not sure where to start?
              </h2>
              <p className="text-blue-100 mb-8">
                Tell us what you're trying to build. We'll tell you which shape fits.
              </p>
              <Link
                to="/contact"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="inline-flex items-center px-6 md:px-8 py-3 md:py-4 bg-white text-blue-600
                           rounded-xl text-base md:text-lg font-semibold hover:bg-blue-50 transition-all
                           shadow-lg hover:shadow-xl hover:translate-y-[-1px]"
              >
                Start a conversation
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PricingPage;
